/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from "react";
import interact from "interactjs";
import {
  tap,
  moveObject,
  editObject,
  interactionStart,
} from "../reducers/slices/phases.js";
import Helper from "../functions/Helper.js";
import useCurrentValue from "../../hooks/useCurrentValue.js";
import DragLine from "./DragLine.js";
import DragCircle from "./DragCircle.js";

import { useTheme } from "@mui/material/styles";

export default function Rectangle(props) {
  const dispatch = props.dispatch;
  const propsRef = useCurrentValue(props);

  const theme = useTheme();

  useEffect(() => {
    if (props.editable) {
      interact("#" + props.uId)
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          if (propsRef.current.status !== "EDIT") {
            dispatch(
              tap({
                id: props.id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("tap", (event) => {
          if (propsRef.current.status !== "EDIT") {
            dispatch(
              tap({
                id: props.id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("doubletap", (event) => {
          if (propsRef.current.status !== "EDIT") {
            dispatch(
              editObject({ actionId: props.id, changes: { status: "EDIT" } })
            );
          }
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onend: (event) => {
            let metaKey = event?.metaKey || event?.ctrlKey;

            let nextPos = [
              Helper.roundNumber(
                propsRef.current.pos[0] + dragDeltaRef.current.x,
                metaKey ? 0 : 2
              ),
              Helper.roundNumber(
                propsRef.current.pos[1] + dragDeltaRef.current.y,
                metaKey ? 0 : 2
              ),
            ];

            dispatch(
              moveObject({
                data: {
                  pos: nextPos,
                },
                id: props.id,
              })
            );

            setEditMode(null);
            setDragDelta({ x: 0, y: 0 });
            dragDeltaRef.current = { x: 0, y: 0 };
          },
          onmove: (event) => {
            var deltaX = event.dx / propsRef.current.pixelPerFoot;
            var deltaY = event.dy / propsRef.current.pixelPerFoot;
            dragDeltaRef.current.x += deltaX;
            dragDeltaRef.current.y += deltaY;

            let metaKey = event?.metaKey || event?.ctrlKey;

            let nextPos = [
              Helper.roundNumber(
                propsRef.current.pos[0] + dragDeltaRef.current.x,
                metaKey ? 0 : 2
              ),
              Helper.roundNumber(
                propsRef.current.pos[1] + dragDeltaRef.current.y,
                metaKey ? 0 : 2
              ),
            ];

            setEditMode("DRAG");
            setDragDelta({
              x: nextPos[0] - propsRef.current.pos[0],
              y: nextPos[1] - propsRef.current.pos[1],
            });

            try {
              document.getElementById("selection_coordinates_x").value =
                nextPos[0];
              document.getElementById("selection_coordinates_y").value =
                nextPos[1];
            } catch (e) {}
          },
          onstart: () => {
            dispatch(interactionStart({ id: props.id }));
          },
        });
      return () => interact("#" + props.uId).unset();
    }
  }, []); //eslint-disable-line

  const handeRotateStart = (event) => {
    const rectElement = document.querySelector("#" + props.uId);
    const rect = rectElement.getBoundingClientRect();
    const centerPageCoords = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    setRotateData({
      centerPageCoords,
      startAngle: Helper.getAngle(centerPageCoords, {
        x: event.clientX,
        y: event.clientY,
      }),
      clientCoords: {
        x: event.clientX,
        y: event.clientY,
      },
    });
    setEditMode("ROTATE");
  };

  const handleRotateMove = (event) => {
    setRotateData((oldData) => ({
      ...oldData,
      clientCoords: {
        x: event.clientX,
        y: event.clientY,
      },
    }));
  };

  const handleRotateEnd = (event) => {
    setEditMode(null);
    setRotateData(null);

    let tempAngle = Helper.getAngle(
      rotateDataRef.current.centerPageCoords,
      rotateDataRef.current.clientCoords
    );

    let nextAngle =
      tempAngle - rotateDataRef.current.startAngle + propsRef.current.angle;

    dispatch(
      editObject({
        actionId: props.id,
        changes: {
          angle: Math.round(nextAngle),
        },
      })
    );
  };

  const handleResizeMove = (eventDelta, event, edge) => {
    if (event.shiftKey && edge.includes("-")) {
      setEditMode("RESIZE_SQUARE");
    } else {
      setEditMode("RESIZE");
    }
    setDragDelta(eventDelta);
  };

  const handleResizeEnd = (eventDelta, event, edge) => {
    let { x, y } = eventDelta;
    let rotatedDragVector = Helper.rotatePointAroundCenter(
      { x, y },
      -propsRef.current.angle,
      { x: 0, y: 0 }
    );

    let {
      x: posX,
      y: posY,
      width,
      height,
    } = Helper.convertRectToSVG(
      propsRef.current.pos[0],
      propsRef.current.pos[1],
      propsRef.current.width,
      propsRef.current.height,
      propsRef.current.court.full
    );

    if (event.shiftKey && edge.includes("-")) {
      rotatedDragVector = Helper.getKeepAspectRatioResizeVector(
        rotatedDragVector,
        edge,
        width,
        height
      );
    }

    let nextRect = Helper.moveRect(
      rotatedDragVector,
      edge,
      posX,
      posY,
      height,
      width
    );
    if (!nextRect) return;

    let oldTransformationOrigin = Helper.getCenter(posX, posY, width, height);

    let centerBeforeRotation = Helper.getCenter(
      nextRect.x,
      nextRect.y,
      nextRect.width,
      nextRect.height
    );

    let centerAfterRotation = Helper.rotatePointAroundCenter(
      { x: centerBeforeRotation[0], y: centerBeforeRotation[1] },
      propsRef.current.angle,
      { x: oldTransformationOrigin[0], y: oldTransformationOrigin[1] }
    );

    let finalRect = Helper.convertRectFromSVG(
      centerAfterRotation.x - nextRect.width / 2,
      centerAfterRotation.y - nextRect.height / 2,
      nextRect.width,
      nextRect.height,
      propsRef.current.court.full
    );

    dispatch(
      editObject({
        actionId: props.id,
        changes: {
          pos: [finalRect.x, finalRect.y],
          width: finalRect.width,
          height: finalRect.height,
        },
      })
    );

    setDragDelta({ x: 0, y: 0 });
    setActiveEdge(null);
    setEditMode(null);
  };

  const getResizeProps = (edge, angle) => ({
    onMove: (dragDelta, event) => {
      handleResizeMove(dragDelta, event, edge);
    },
    onStart: () => {
      setActiveEdge(edge);
    },
    onEnd: (dragDelta, event) => {
      handleResizeEnd(dragDelta, event, edge);
    },
    cursor: {
      type: "resize",
      angle: angle,
      edge: edge,
    },
  });

  const effectiveStatus = Helper.getEffectiveStatus(props);

  let showDragCircles = ["SELECTED", "INTERACTING", "EDIT"].includes(
    effectiveStatus
  );

  const showDragLines = [
    "SELECTED",
    "SELECTED_MULTIPLE",
    "INTERACTING",
    "EDIT",
    "HOVERING",
    "HOVERING_ANIMATOR",
  ].includes(effectiveStatus);

  let {
    x: posX,
    y: posY,
    width,
    height,
  } = Helper.convertRectToSVG(
    props.pos[0],
    props.pos[1],
    props.width,
    props.height,
    props.court.full
  );

  let angle = props.angle;
  let [centerX, centerY] = Helper.getCenter(posX, posY, width, height);
  const [dragDelta, setDragDelta] = useState({ x: 0, y: 0 });
  const [activeEdge, setActiveEdge] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [rotateData, setRotateData] = useState(null);
  const rotateDataRef = useCurrentValue(rotateData);
  const dragDeltaRef = useRef({ x: 0, y: 0 });
  const translate = { x: 0, y: 0 };

  if (editMode === "RESIZE" || editMode === "RESIZE_SQUARE") {
    let rotatedDragVector = Helper.rotatePointAroundCenter(dragDelta, -angle, {
      x: 0,
      y: 0,
    });
    if (editMode === "RESIZE_SQUARE") {
      rotatedDragVector = Helper.getKeepAspectRatioResizeVector(
        rotatedDragVector,
        activeEdge,
        width,
        height
      );
    }

    let nextRect = Helper.moveRect(
      rotatedDragVector,
      activeEdge,
      posX,
      posY,
      height,
      width
    );

    if (nextRect) {
      posX = nextRect.x;
      posY = nextRect.y;
      width = nextRect.width;
      height = nextRect.height;
    }
  }

  if (editMode === "DRAG") {
    translate.x = dragDelta.x;
    translate.y = dragDelta.y;
  }

  if (editMode === "ROTATE") {
    let tempAngle = Helper.getAngle(
      rotateData.centerPageCoords,
      rotateData.clientCoords
    );
    angle = tempAngle - rotateData.startAngle + props.angle;
  }

  return (
    <g
      id={props.uId}
      transform={`translate(${translate.x} ${translate.y}) rotate(${angle} ${centerX} ${centerY})`}
      data-rotate={`rotate(${angle}deg)`}
      data-transform-origin={`${centerX}px ${centerY}px`}
    >
      <rect
        /*
        onMouseEnter={() => {
          if (
            props.editable &&
            props.status === "NOT_SELECTED" &&
            !props.interaction
          ) {
            dispatch(mouseEnter({ id: props.id }));
          }
        }}
        onMouseLeave={() => {
          if (
            props.editable &&
            props.status === "HOVERING" &&
            !props.interaction
          ) {
            dispatch(mouseLeave({ id: props.id }));
          }
        }}*/
        onClick={(e) => {
          if (props.editable) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        className={
          "draggable " + (props.linkedObjectHovering ? "linked_hovering " : "")
        }
        id={props.uId + "_rect"}
        style={{
          cursor: "move",
          fill: props.backgroundColor,
        }}
        x={posX}
        y={posY}
        height={height}
        width={width}
        stroke="black"
        strokeWidth={0}
      />

      {showDragLines && (
        <>
          <DragLine
            {...getResizeProps("top", angle)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY}
            x2={posX + width}
            y2={posY}
          />

          <DragLine
            {...getResizeProps("bottom", angle)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY + height}
            x2={posX + width}
            y2={posY + height}
          />

          <DragLine
            {...getResizeProps("left", angle)}
            pixelPerFoot={props.pixelPerFoot}
            x1={posX}
            y1={posY + height}
            x2={posX}
            y2={posY}
          />

          <DragLine
            {...getResizeProps("right", angle)}
            pixelPerFoot={props.pixelPerFoot}
            edge="right"
            x1={posX + width}
            y1={posY}
            x2={posX + width}
            y2={posY + height}
          />
        </>
      )}

      {showDragCircles && (
        <>
          <DragCircle
            {...getResizeProps("top-right", angle)}
            x={posX + width}
            y={posY}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            {...getResizeProps("top-left", angle)}
            x={posX}
            y={posY}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            {...getResizeProps("bottom-right", angle)}
            x={posX + width}
            y={posY + height}
            pixelPerFoot={props.pixelPerFoot}
          />

          <DragCircle
            {...getResizeProps("bottom-left", angle)}
            x={posX}
            y={posY + height}
            pixelPerFoot={props.pixelPerFoot}
          />

          <line
            x1={posX + width / 2}
            y1={posY}
            x2={posX + width / 2}
            y2={posY - (props.court.full ? 3 : 2)}
            stroke={theme.palette.active}
            strokeWidth={2 / props.pixelPerFoot}
          />

          <DragCircle
            color="white"
            onStart={(e) => {
              handeRotateStart(e);
            }}
            onMove={(dragDelta, e) => {
              handleRotateMove(e);
            }}
            onEnd={(dragDelta, e) => {
              handleRotateEnd(e);
            }}
            pixelPerFoot={props.pixelPerFoot}
            x={posX + width / 2}
            y={posY - (props.court.full ? 3 : 2)}
          />
        </>
      )}
    </g>
  );
}
